/**
 * LiveRoom — Native webinar host view powered by 100ms.live.
 *
 * Route: /app/webinars/:id/live-room  (protected — creator/host only)
 *
 * Responsibilities:
 *   • Fetch host auth token from backend
 *   • Wrap children in HMSRoomProvider (auto-injected globally in App.js is
 *     not desirable — we scope it here so guests routes don't pay the cost)
 *   • Provide preview → join → in-room UI with camera/mic/screen controls,
 *     participant list, live attendance counter, and recording toggle
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { HMSRoomProvider } from "@100mslive/react-sdk";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, AlertTriangle } from "lucide-react";

import LiveStage from "@/components/live/LiveStage";

export default function LiveRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [joinInfo, setJoinInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: w }, { data: cfg }] = await Promise.all([
          api.get(`/webinars/${id}`),
          api.get(`/live/config`),
        ]);
        setWebinar(w);
        if (!cfg.enabled) {
          setError("Native live is not configured on this server. Please contact support.");
          return;
        }
        // Ensure room + fetch host token
        await api.post(`/webinars/${id}/live/room`);
        const { data: tk } = await api.post(`/webinars/${id}/live/token`);
        setJoinInfo(tk);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [id]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto vp-card p-6 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 mx-auto text-[#B00020]" />
        <div className="font-display font-bold">Could not start live session</div>
        <p className="text-sm text-[#4A4D57]">{error}</p>
        <Link to={`/app/webinars/${id}`} className="vp-btn-ghost inline-flex items-center gap-1.5 text-sm py-2 px-3">
          <ArrowLeft className="w-4 h-4" /> Back to webinar
        </Link>
      </div>
    );
  }

  if (!joinInfo || !webinar) {
    return <div className="p-6 text-[#4A4D57]">Preparing live studio…</div>;
  }

  return (
    <HMSRoomProvider>
      <LiveStage
        role="host"
        webinar={webinar}
        joinInfo={joinInfo}
        onExit={() => nav(`/app/webinars/${id}`)}
      />
    </HMSRoomProvider>
  );
}
